import { ReactNode } from 'react';
import {
  CSSObjectWithLabel,
  ControlProps,
  GroupBase,
  IndicatorsContainerProps,
  OptionProps,
} from 'react-select';
import {
  body2,
  borderColors,
  borderRadius,
  colors,
  fonts,
  mediumShadow,
  spacing,
  subtleShadow,
} from 'ui/theme';
import { zIndex } from 'ui/theme/typography/styles';

export const DefaultTheme = (errorMsg?: ReactNode) => {
  const additionalControlStyles = errorMsg
    ? { '&, &:hover': { border: `1px solid ${borderColors.borderDestructive}` } }
    : {
        '&:hover': {
          border: `1px solid ${borderColors.borderInteractive}`,
        },
      };
  return {
    control: (
      baseStyles: CSSObjectWithLabel,
      state?: ControlProps<any, true, GroupBase<unknown>>,
    ) => ({
      ...baseStyles,
      ...subtleShadow,
      ...additionalControlStyles,
      minHeight: 40,
      ...(state?.isDisabled && {
        '&:hover': {},
        'pointer-events': 'auto',
        cursor: 'not-allowed',
      }),
    }),
    multiValue: (baseStyles: CSSObjectWithLabel) => ({
      ...baseStyles,
      borderRadius: borderRadius.md,
      background: colors.backgroundSecondary,
      border: `1px solid ${borderColors.borderPrimary}`,
      padding: `${spacing.quarter} ${spacing.half}`,
      '&:hover': {
        background: colors.backgroundInteractive,
        border: `1px solid ${borderColors.borderInteractive}`,
        ' > div': {
          color: colors.linkActive,
        },
        '& svg > *': {
          stroke: colors.iconInteractive,
        },
      },
      '& > div': {
        padding: 0,
      },
    }),
    option: (
      baseStyles: CSSObjectWithLabel,
      state: OptionProps<any, true, GroupBase<unknown>>,
    ) => ({
      ...baseStyles,
      ...body2,
      alignItems: 'center',
      border: `solid ${spacing.single} transparent`,
      borderRadius: `${spacing.quarter}`,
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: `${spacing.single}`, // keeps focus state from causing overlaps
      overflow: 'hidden',
      outline: 'none',
      padding: `${spacing.half}`,
      '&&&': {
        backgroundColor: state.isFocused ? colors.backgroundPrimaryHover : 'inherit',
      },
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      '&&.select-all': {
        width: 'fit-content',
        position: 'absolute',
        right: 0,
        top: 0,
        padding: `0 ${spacing.half}`,
        marginTop: spacing.eighth,
        color: colors.textSecondary,
      },
    }),
    menu: (baseStyles: CSSObjectWithLabel) => ({
      ...baseStyles,
      ...mediumShadow,
      borderRadius: borderRadius.lg,
    }),
    menuPortal: (baseStyles: CSSObjectWithLabel) => ({ ...baseStyles, zIndex: zIndex.popover }), // supports proper hierarchy with menu portal
    placeholder: (baseStyles: CSSObjectWithLabel) => ({
      ...baseStyles,
      fontFamily: `${fonts.sansSerif}`,
    }),
    valueContainer: (baseStyles: CSSObjectWithLabel) => ({
      ...baseStyles,
      padding: `${spacing.eighth} ${spacing.half}`,
    }),
    menuList: (baseStyles: CSSObjectWithLabel) => ({
      ...baseStyles,
      padding: `${spacing.threeEights}`,
      '> div': {
        position: 'relative',
      },
    }),
    multiValueRemove: (baseStyles: CSSObjectWithLabel) => ({
      ...baseStyles,
      '&&&&': {
        paddingLeft: spacing.threeEights,
      },
      '&:hover': {
        backgroundColor: 'inherit',
        color: 'inherit',
      },
    }),
    multiValueLabel: (baseStyles: CSSObjectWithLabel) => ({
      ...baseStyles,
      ...body2,
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    indicatorsContainer: (baseStyles: CSSObjectWithLabel, state?: IndicatorsContainerProps) => ({
      ...baseStyles,
      cursor: state?.isDisabled ? 'not-allowed' : 'pointer',
    }),
    input: (baseStyles: CSSObjectWithLabel) => ({
      ...baseStyles,
      ...body2,
      input: {
        boxShadow: 'none',
      },
    }),
    singleValue: (baseStyles: CSSObjectWithLabel) => ({
      ...baseStyles,
      ...body2,
    }),
    group: (baseStyles: CSSObjectWithLabel) => ({
      ...baseStyles,
      padding: 0,
    }),
  };
};

export const DefaultPopoverTheme = {
  control: (baseStyles: CSSObjectWithLabel) => ({
    ...baseStyles,
    ...subtleShadow,
    minHeight: 40,
    minWidth: 240,
    margin: 8,
    border: `1px solid ${borderColors.borderPrimary}`,
    '&:active': { border: `1px solid ${borderColors.borderInteractive}` },
    '&:focus': { border: `1px solid ${borderColors.borderInteractive}` },
    '&:hover': {
      border: `1px solid ${borderColors.borderInteractive}`,
    },
  }),
  menu: () => ({}),
  menuList: (baseStyles: CSSObjectWithLabel) => ({
    ...baseStyles,
    padding: `${spacing.quarter} ${spacing.threeEights}`,
    '> div': {
      position: 'relative',
    },
  }),
  option: (baseStyles: CSSObjectWithLabel, state: OptionProps<any, true, GroupBase<unknown>>) => ({
    ...baseStyles,
    ...body2,
    alignItems: 'center',
    border: `solid ${spacing.single} transparent`,
    borderRadius: `${spacing.quarter}`,
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: `${spacing.single}`, // keeps focus state from causing overlaps
    overflow: 'hidden',
    outline: 'none',
    padding: `${spacing.half}`,
    '&&&': {
      backgroundColor: state.isFocused ? colors.backgroundPrimaryHover : 'inherit',
    },
    cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    '&&.select-all': {
      width: 'fit-content',
      position: 'absolute',
      right: 0,
      top: 0,
      padding: `0 ${spacing.half}`,
      marginTop: spacing.eighth,
      color: colors.textSecondary,
    },
  }),
  indicatorsContainer: (baseStyles: CSSObjectWithLabel) => ({
    ...baseStyles,
    cursor: 'pointer',
  }),
};
